import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Inject,
	OnDestroy,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation,
} from "@angular/core";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { LeftMenuSelector } from "@obg/ngx.core";
import { ComponentFactoryService } from "@obg/ngx.ui.core";
import { ignoreFalsy } from "@obg/rxjs";
import { EMPTY, merge, Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { NavbarOptions } from "../../navbar/navbar.model";
import { DialogComponentData, DialogLike } from "../dialog.model";

@Component({
	templateUrl: "./dialog-like.container.html",
	styleUrls: ["./dialog-like.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class DialogLikeContainer implements OnDestroy {
	@ViewChild("view", { read: ViewContainerRef, static: true })
	set view(value: ViewContainerRef | undefined) {
		this.initializeView(value);
	}

	@HostBinding("class.obg-m-dialog") hostCssClass = true;

	isBusy = false;
	busyMessage: string | undefined;
	title: string | undefined;
	hideClose: boolean | undefined;
	hideFooter: boolean | undefined;
	navbar: boolean | undefined;
	navbarOptions: NavbarOptions | undefined;

	private data$$: Subscription | undefined;

	constructor(
		// it cannot be `DialogComponentData & { dialogLike: DialogLike<any> }`
		// because it doesn't compile (due to strictMetadataEmit switch in tsconfig).
		// Also `{ dialogLike: DialogLike<any> }` cannot be part of DialogComponentData
		@Inject(MAT_DIALOG_DATA) private data: any,
		private compenentFactoryResolver: ComponentFactoryService,
		private changeDetectorRef: ChangeDetectorRef,
		private dialogRef: MatDialogRef<any>,
		private store: Store
	) {
		const settings = data as DialogComponentData;
		this.title = settings.title;
		this.hideClose = settings.hideClose;
		this.hideFooter = settings.hideFooter;
		this.navbar = settings.navbar;
		this.navbarOptions = settings.navbarOptions;
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	private initializeView(view: ViewContainerRef | undefined) {
		const dialogLike = this.data.dialogLike as DialogLike<any>;
		if (!dialogLike) {
			throw new Error("Dialog like component expected");
		}

		if (!view) {
			return;
		}

		const ref = view.createComponent(this.compenentFactoryResolver.get(dialogLike.dialogComponentType));
		if (dialogLike.init) {
			dialogLike.init(ref.instance);
		}
		ref.changeDetectorRef.markForCheck();

		const busy$ = (dialogLike.onBusy$?.(ref.instance) || EMPTY).pipe(
			tap((busy) => ([this.isBusy, this.busyMessage] = [busy.isBusy, busy.message]))
		);

		const close$ = ((this.dialogRef && dialogLike.onClose$?.(ref.instance)) || EMPTY).pipe(
			tap(() => this.dialogRef.close())
		);

		const closeOnLeftMenuOpen$ = this.store.select(LeftMenuSelector.isOpen).pipe(
			ignoreFalsy(),
			tap(() => this.dialogRef.close())
		);

		// markForChanges doesn't always work here.
		this.data$$ = merge(busy$, close$, closeOnLeftMenuOpen$).subscribe(() =>
			this.changeDetectorRef.detectChanges()
		);
	}
}
